import * as React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useNavigate } from '@tanstack/react-router';
import { ChevronDown, LogOut, Settings, User } from 'lucide-react';

import { useLogout, type SessionInfo } from '@/lib/auth';

export interface UserMenuProps {
  session: SessionInfo;
}

export function UserMenu({ session }: UserMenuProps) {
  const logout = useLogout();
  const navigate = useNavigate();

  const initials = (session.user.full_name ?? session.user.email)
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]!.toUpperCase())
    .join('');

  const onLogout = async () => {
    await logout.mutateAsync();
    navigate({ to: '/login' });
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger className="flex items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
        <span
          aria-hidden="true"
          className="grid size-7 place-items-center rounded-full bg-accent/10 text-xs font-semibold text-accent ring-1 ring-accent/20"
        >
          {initials}
        </span>
        <span className="hidden text-left sm:block">
          <span className="block leading-tight text-text">
            {session.user.full_name ?? session.user.email}
          </span>
          <span className="block text-xs text-text-subtle">
            {session.role === 'operator' ? 'Operator' : (session.tenant?.legal_name ?? '')}
          </span>
        </span>
        <ChevronDown className="size-3.5 text-text-subtle" aria-hidden="true" />
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 min-w-[12rem] rounded-md bg-surface-2 p-1 ring-1 ring-black/10 shadow-lg"
        >
          <DropdownMenu.Item
            className="flex items-center gap-2 rounded px-2 py-2 text-sm text-text-subtle cursor-not-allowed"
            disabled
          >
            <User className="size-4" /> Profil
            <span className="ml-auto text-[10px] uppercase">wkrótce</span>
          </DropdownMenu.Item>
          {session.role !== 'operator' ? (
            <DropdownMenu.Item
              className="flex items-center gap-2 rounded px-2 py-2 text-sm text-text-subtle cursor-not-allowed"
              disabled
            >
              <Settings className="size-4" /> Ustawienia
              <span className="ml-auto text-[10px] uppercase">wkrótce</span>
            </DropdownMenu.Item>
          ) : null}
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Item
            className="flex items-center gap-2 rounded px-2 py-2 text-sm text-text hover:bg-surface focus:outline-none cursor-pointer"
            onSelect={onLogout}
          >
            <LogOut className="size-4" /> Wyloguj się
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
